
import {Component, Inject, Prop, Vue, Watch} from "vue-property-decorator";
	import AccomodationService from "@/services/AccomodationService";
	import ApiHelper from "@/helpers/api-helper";
	import {contarPedido, ESPERA_DE_PRECIOS, EsperaDePrecios} from "@/helpers/espera-de-precios";
import FormatHelper from "@/helpers/format-helper";
import {tarifaCubreFechas, viajeYaEmpezado} from "@/helpers/tramo-helper";
import {CambiosAlGuardar, sinCambios} from "@/helpers/ventana-al-guardar";

	/** El id de la tarifa elegida: es el id al abrir el presupuesto y el objeto tras elegir en la lista. */
	function idDe(value: any): any {
		return value && typeof value === 'object' ? value['id'] : value;
	}

	/** «A», «A y B», «A, B y C». */
	function enumerar(nombres: string[]): string {
		return nombres.length > 1 ? nombres.slice(0, -1).join(', ') + ' y ' + nombres[nombres.length - 1] : nombres.join('');
	}

	@Component({})
	export default class AccommodationPriceSelect extends Vue {
		// N-288 · 4A: GUARDAR espera mientras el catálogo de precios está en camino.
		@Inject({ from: ESPERA_DE_PRECIOS, default: null }) private readonly esperaDePrecios!: EsperaDePrecios | null;
		@Prop({ default: null, required: false }) private model;
		@Prop({ default: null, required: true }) private errors;
		@Prop({ default: null, required: true }) private accommodation_id;
		@Prop({ default: null, required: true }) private feature;
		@Prop({ default: null, required: true }) private increases;
		@Prop({ default: null, required: true }) private people;
		// N-287 B: las fechas del viaje (la tarifa tiene que cubrirlas, como en el funnel), el nombre
		// del alojamiento, lo que tenía guardado y los alojamientos del catálogo, para decir cuál
		// sirve. El editor de paquetes no los pasa: allí la tarifa solo sigue a las personas.
		@Prop({ default: null, required: false }) private start_date;
		@Prop({ default: null, required: false }) private end_date;
		@Prop({ default: null, required: false }) private accommodation_name;
		@Prop({ default: null, required: false }) private guardado;
		@Prop({ default: null, required: false }) private alojamientos;

		private items: any[] = [];
		private aux_items: any[] = [];
		// Hasta que llega el primer catálogo no se sabe qué tarifas cubren: no se elige ni se quita nada.
		private cargado = false;
		private value: any = null;
		private filters: any;
		// N-287 B: la tarifa que pidió el cliente (o eligió el operador a mano). Se vuelve a ella en
		// cuanto cubre otra vez el viaje, para que un número intermedio no la haga olvidar.
		private tarifaPedida: any = null;
		// Todas las tarifas vistas: dicen hasta dónde llega cada alojamiento.
		private tarifasVistas: any = {};
		// Lo que se le dice al operador debajo del campo (7B · B2A): {tipo: 'info' | 'error', texto}.
		private aviso: any = null;
		// Sin ninguna tarifa que cubra, el principio del mensaje: el final dice qué otro alojamiento sirve.
		private sinTarifa: string | null = null;
		// Las tarifas de TODOS los alojamientos para un número de personas, pedidas una sola vez:
		// buscar qué otro sirve no puede lanzar una petición por hotel (la API corta a 60 por minuto).
		private tarifasDeTodos: any = {};

		@Watch("accommodation_id")
		accommodation_id_change(value) {
			this.filters.accommodation_id = value;
			if (this.filters.accommodation_id && this.filters.people != null) {
				this.getSelectorList();
			}
		}

		@Watch("people")
		people_change(value) {
			this.filters.people = value;
			if (this.filters.accommodation_id && this.filters.people != null) {
				this.getSelectorList();
			}
		}

		// N-287 B: el catálogo no depende de las fechas (el servidor da todas las temporadas); al
		// moverlas basta con volver a mirar cuáles cubren el viaje. Llegada y salida cambian a la vez:
		// un solo vigilante para las dos.
		get fechas() {
			return [this.start_date, this.end_date].join('|');
		}

		@Watch("fechas")
		fechas_change() {
			this.reelegir();
		}

		@Watch("alojamientos")
		alojamientos_change() {
			if (this.sinTarifa !== null) {
				this.buscarAlternativas();
			}
		}

		// N-241: el precio de cada tarifa lleva el suplemento del régimen, así que al
		// cambiar el régimen —o al llegar los suplementos— hay que recalcularlo Y
		// volver a emitir el de la tarifa elegida. Antes solo se repintaba la lista y
		// el presupuesto se guardaba con el precio del régimen anterior.
		@Watch("feature")
		feature_change() {
			this.prepareItems(this.aux_items);
			this.emitSelected();
			this.rehacerElegidaSola();
		}

		// N-241: los suplementos (`increases`) llegan en otra petición, la de
		// `AccommodationSelect`, que compite con la de las tarifas. Si las tarifas
		// llegaban antes, el precio salía `tarifa + undefined = NaN` y viajaba vacío
		// al backend (presupuesto 104932). Cuando llegan, se recalcula.
		@Watch("increases")
		increases_change() {
			this.prepareItems(this.aux_items);
			this.emitSelected();
			this.rehacerElegidaSola();
		}

		constructor() {
			super();
			this.filters = { accommodation_id: null, people: null };
		}

		get erroresVisibles() {
			return this.aviso && this.aviso.tipo === 'error' ? [this.aviso.texto] : this.errors;
		}

		get mensajesVisibles() {
			return this.aviso && this.aviso.tipo === 'info' ? [this.aviso.texto] : [];
		}

		mounted() {
			if (this.model) {
				this.value = this.model;
				this.tarifaPedida = idDe(this.model);
			}
			if (this.accommodation_id) {
				this.filters.accommodation_id = this.accommodation_id;
			}
			if (this.people != null) {
				this.filters.people = this.people;
			}
			if (this.filters.accommodation_id && this.filters.people != null) {
				this.getSelectorList();
			}
		}

		getSelectorList() {
			const pedidoCon = JSON.stringify(this.filters);
			contarPedido(this.esperaDePrecios, AccomodationService.getSelectorPriceListGroup(null, this.filters)
				.then((res) => {
					// N-287: una respuesta vieja (el «3» de «30», el hotel de antes) no pisa a la buena
					// ni vuelve a emitir el precio de otro tramo.
					if (pedidoCon !== JSON.stringify(this.filters)) {
						return;
					}
					if (res.status === 200) {
						this.aux_items = res.data;
						this.cargado = true;
						for (const fecha in res.data) {
							for (const tarifa of res.data[fecha]) {
								this.tarifasVistas[tarifa['id']] = tarifa;
							}
						}
						this.reelegir();
					}
				})
				.catch((error) => {
					ApiHelper.handleErrors(error, {}, this.$bus);
				}));
		}

		/**
		 * N-287 B · La regla del funnel con la decisión 7B. Sigue la tarifa pedida si cubre las fechas
		 * y las personas; si no, y solo una cubre, se pone sola y lo dice; si hay varias o ninguna,
		 * se vacía (GUARDAR no deja seguir) y se pide diciendo lo que tenía.
		 */
		reelegir() {
			if (!this.cargado) {
				return;
			}
			this.prepareItems(this.aux_items);
			this.sinTarifa = null;
			// Un viaje que ya empezó se queda como estaba (decisión de Bernardo): ni se elige ni se quita.
			if (viajeYaEmpezado(this.start_date)) {
				this.aviso = null;
				this.emitSelected();
				return;
			}
			const cubren = this.items.filter((item) => item.id);
			if (this.tarifaPedida && cubren.some((item) => item.id == this.tarifaPedida)) {
				this.aviso = null;
				this.poner(this.tarifaPedida);
				return;
			}
			if (cubren.length === 1) {
				this.poner(cubren[0].id);
				this.aviso = { tipo: 'info', texto: this.textoElegidaSola(cubren[0]) };
				return;
			}
			this.quitar();
			if (cubren.length > 1) {
				this.aviso = { tipo: 'error', texto: this.textoVarias(cubren.length) };
				return;
			}
			this.sinTarifa = this.textoNinguna();
			this.aviso = { tipo: 'error', texto: this.sinTarifa };
			this.buscarAlternativas();
		}

		poner(id) {
			if (idDe(this.value) != id) {
				this.value = id;
				this.$emit('input', id);
			}
			this.emitSelected();
		}

		/** El «Elegida sola» compara precios: si el régimen o sus suplementos llegan después, se rehace. */
		rehacerElegidaSola() {
			if (!this.aviso || this.aviso.tipo !== 'info') {
				return;
			}
			const elegida = this.items.find((item) => item.id && item.id == idDe(this.value));
			if (elegida) {
				this.aviso = { tipo: 'info', texto: this.textoElegidaSola(elegida) };
			}
		}

		quitar() {
			if (idDe(this.value) != null) {
				this.value = null;
				this.$emit('input', null);
			}
		}

		get rango() {
			return this.start_date && this.end_date
				? 'del ' + FormatHelper.formatDate(String(this.start_date).substring(0, 10)) + ' al ' + FormatHelper.formatDate(String(this.end_date).substring(0, 10))
				: '';
		}

		get personasYFechas() {
			return this.rango ? this.rango + ' y ' + this.people + ' personas' : this.people + ' personas';
		}

		/** Lo que tenía guardado el presupuesto, para que el operador sepa de dónde viene. */
		get textoTenia() {
			const guardado = this.guardado;
			if (!guardado || guardado.price == null || !isFinite(Number(guardado.price))) {
				return '';
			}
			if (guardado.price_id && guardado.people_min != null) {
				return 'Tenía «De ' + guardado.people_min + ' a ' + guardado.people_max + ' personas», ' + FormatHelper.euros(guardado.price) + ' €/noche.';
			}
			return 'Tenía ' + FormatHelper.euros(guardado.price) + ' €/noche, sin tarifa enlazada.';
		}

		textoElegidaSola(item) {
			let texto = 'Elegida sola: es la única que cubre ' + this.personasYFechas + '.';
			const guardado = this.guardado;
			if (guardado && guardado.price != null && isFinite(Number(guardado.price)) && isFinite(item.price)) {
				texto += Number(guardado.price) == Number(item.price)
					? ' Mismo precio que tenía: ' + FormatHelper.euros(item.price) + ' €/noche.'
					: ' Tenía ' + FormatHelper.euros(guardado.price) + ' €/noche; ahora, ' + FormatHelper.euros(item.price) + ' €/noche.';
			}
			return texto;
		}

		textoVarias(cuantas) {
			return (this.textoTenia ? this.textoTenia + ' ' : '') + 'Elige la tarifa: para ' + this.personasYFechas + ' sirven ' + cuantas + '.';
		}

		/** B2A: por qué no hay tarifa (hasta dónde llega el hotel) y lo que tenía. */
		textoNinguna() {
			const nombre = this.accommodation_name ? 'El ' + this.accommodation_name : 'Este alojamiento';
			let texto = nombre + ' no tiene tarifa para ' + this.people + ' personas' + (this.rango ? ' ' + this.rango : '');
			const motivo = this.motivoSinTarifa();
			texto += (motivo ? ' (' + motivo + ')' : '') + '.';
			return this.textoTenia ? texto + ' ' + this.textoTenia : texto;
		}

		motivoSinTarifa() {
			const delTramo: any[] = [];
			for (const fecha in this.aux_items) {
				delTramo.push(...this.aux_items[fecha]);
			}
			const dia = (fecha) => String(fecha || '').substring(0, 10);
			if (delTramo.length) {
				// Hay tarifas para estas personas, pero ninguna cubre las fechas.
				const ultima = delTramo.map((tarifa) => dia(tarifa.date_end)).sort()[delTramo.length - 1];
				if (ultima < dia(this.end_date)) {
					return 'vale hasta el ' + FormatHelper.formatDate(ultima);
				}
				const primera = delTramo.map((tarifa) => dia(tarifa.date_start)).sort()[0];
				return primera > dia(this.start_date) ? 'empieza el ' + FormatHelper.formatDate(primera) : '';
			}
			// Hasta dónde llega ESTE alojamiento, por sus tarifas ya vistas (y la guardada, si es suya);
			// las de otro hotel, antes de cambiarlo a mano, no sirven.
			const vistas: any[] = Object.keys(this.tarifasVistas).map((id) => this.tarifasVistas[id])
				.filter((tarifa) => tarifa.accommodation_id == this.accommodation_id);
			if (this.guardado && this.guardado.accommodation_id == this.accommodation_id && this.guardado.people_max != null) {
				vistas.push(this.guardado);
			}
			if (vistas.length) {
				const tope = Math.max(...vistas.map((tarifa) => tarifa.people_max));
				const suelo = Math.min(...vistas.map((tarifa) => tarifa.people_min));
				if (this.people > tope) {
					return 'llega a ' + tope;
				}
				if (this.people < suelo) {
					return 'empieza en ' + suelo + ' personas';
				}
			}
			return '';
		}

		/**
		 * B2A: qué otro alojamiento del catálogo tiene tarifa para estas personas y estas fechas. El
		 * catálogo de alojamientos ya viene filtrado por personas; las fechas se miran en sus tarifas,
		 * que se piden de una vez para todos (sin `accommodation_id`) y se guardan por número de personas.
		 */
		buscarAlternativas() {
			const candidatos = (this.alojamientos || []).filter((alojamiento) => alojamiento.id != this.accommodation_id);
			const consulta = JSON.stringify([this.filters, this.start_date, this.end_date, candidatos]);
			const terminar = (nombres: string[]) => {
				const vistos = nombres.filter((nombre, i) => nombre && nombres.indexOf(nombre) === i);
				const remedio = this.rango ? 'cambia el número o las fechas en «Destino y cliente»' : 'cambia el número de personas';
				const final = vistos.length
					? 'Elige otro alojamiento: para ' + (this.rango ? 'esas fechas y personas' : 'esas personas') + ' hay ' + enumerar(vistos) + '.'
					: 'Ningún otro alojamiento de este viaje tiene tarifa para ' + (this.rango ? 'esas fechas y personas' : 'esas personas') + ': ' + remedio + '.';
				this.aviso = { tipo: 'error', texto: this.sinTarifa + ' ' + final };
			};
			if (!this.start_date || !this.end_date) {
				terminar(candidatos.map((alojamiento) => alojamiento.name));
				return;
			}
			const people = this.filters.people;
			if (!this.tarifasDeTodos[people]) {
				this.tarifasDeTodos[people] = AccomodationService.getSelectorPriceListGroup(null, { people })
					.then((res) => {
						const tarifas: any[] = [];
						for (const fecha in (res.status === 200 ? res.data : {})) {
							tarifas.push(...res.data[fecha]);
						}
						return tarifas;
					})
					.catch(() => {
						delete this.tarifasDeTodos[people];
						return null;
					});
			}
			this.tarifasDeTodos[people].then((tarifas) => {
				// Si entretanto el operador eligió a mano o cambió algo, esta respuesta ya no vale.
				if (tarifas === null || this.sinTarifa === null
					|| consulta !== JSON.stringify([this.filters, this.start_date, this.end_date, candidatos])) {
					return;
				}
				terminar(candidatos
					.filter((alojamiento) => tarifas.some((tarifa) => tarifa.accommodation_id == alojamiento.id
						&& tarifaCubreFechas(tarifa, this.start_date, this.end_date)))
					.map((alojamiento) => alojamiento.name));
			});
		}

		/**
		 * N-288 · T3 (2A) · Para la ventana de GUARDAR: la tarifa guardada pasó a otra (el tramo o la
		 * temporada nuevos) con otro precio por noche. Solo si el hotel y el régimen son los de antes y la
		 * tarifa la ha puesto el panel: si el operador los cambió o la eligió a mano, es cosa suya.
		 */
		cambiosAlGuardar(): CambiosAlGuardar {
			const guardado = this.guardado;
			const id = idDe(this.value);
			if (!this.cargado || !guardado || guardado.price_id == null || !id || id == guardado.price_id
				|| guardado.accommodation_id != this.accommodation_id || guardado.feature_id != this.feature) {
				return sinCambios();
			}
			const elegida = this.items.find((item) => item.id && item.id == id);
			if (id == this.tarifaPedida || !elegida || !isFinite(elegida.price) || Number(elegida.price) === Number(guardado.price)) {
				return sinCambios();
			}
			return { quitan: [], cambian: [{
				texto: 'Alojamiento · ' + (this.accommodation_name || ''), antes: guardado.price, ahora: elegida.price, unidad: 'la noche',
			}] };
		}

		// Emite el precio de la tarifa elegida, solo si ya es un número: sin los
		// suplementos todavía, esperar es mejor que mandar un NaN (N-241). `value` es
		// el id al abrir el presupuesto y el objeto entero tras elegir en la lista.
		emitSelected() {
			const id = idDe(this.value);
			if (!id) {
				return;
			}
			for (const item of this.items) {
				if (item.id == id && isFinite(item.price)) {
					this.$emit('setPrice', item);
				}
			}
		}

		emitValue(value){
			// N-287 B: lo que el operador elige a mano pasa a ser lo pedido, y el aviso se va.
			this.tarifaPedida = idDe(value);
			this.aviso = null;
			this.sinTarifa = null;
			this.$emit('input', idDe(value));
			if (value) {
				this.$emit('setPrice', value);
			}
		}

		prepareItems(itemsData){
			this.items = [];
			const items: any[] = [];
			let date = ''
			for (const itemKey in itemsData){
				for (const itemData of itemsData[itemKey]){
					// N-287 B: solo las tarifas que cubren el viaje, como en el funnel (salvo si ya empezó).
					if (!viajeYaEmpezado(this.start_date) && !tarifaCubreFechas(itemData, this.start_date, this.end_date)) {
						continue;
					}
					const price = this.feature == 1 ? itemData['price'] :
						(this.feature == 2 ? itemData['price'] + this.increases.half_board : itemData['price'] + this.increases.full_board );
					if(date == itemKey){
						items.push({
							name: 'De ' + itemData['people_min'] + ' a ' + itemData['people_max'] + ' personas ' + price + ' €',
							id: itemData['id'],
							price: price,
							people: itemData['people_max']
						});
					}else{
						date = itemKey;
						items.push({header: 'Desde ' + FormatHelper.formatDate(itemKey) + ' hasta el ' + FormatHelper.formatDate(itemData['date_end'])});
						items.push({
							name: 'De ' + itemData['people_min'] + ' a ' + itemData['people_max'] + ' personas ' + price + ' €',
							id: itemData['id'],
							price: price,
							people: itemData['people_max']
						});
					}
				}
			}
			this.items = items;
		}
	}
